import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/apiClient';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  UsersIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  HeartIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import SlackIcon from '../components/icons/SlackIcon';
import GongIcon from '../components/icons/GongIcon';

// Define the type for our new API response
interface DashboardOverview {
  metrics: {
    active_accounts: number;
    risk_alerts: number;
    expansion_opportunities: number;
    health_score: number;
  };
  integration_statuses: {
    slack: 'connected' | 'disconnected';
    gong: 'connected' | 'disconnected';
  };
  recent_activities: Array<{
    id: string;
    title: string;
    description: string;
    timestamp: string;
    priority: 'high' | 'medium' | 'low';
    query_text: string;
  }>;
  trends: {
    accounts_change: string;
    alerts_change: string;
    opportunities_change: string;
    health_change: string;
  };
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const { data: overview, isLoading } = useQuery<DashboardOverview>({
    queryKey: ['dashboard-overview'],
    queryFn: () => apiClient.customRequest('/api/insights/dashboard/overview'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const metrics = overview?.metrics || {
    active_accounts: 0, risk_alerts: 0, expansion_opportunities: 0, health_score: 0
  };
  const trends = overview?.trends || {
    accounts_change: '0%', alerts_change: '0%', opportunities_change: '0%', health_change: '0'
  };
  const activities = overview?.recent_activities || [];
  const integrations = overview?.integration_statuses || { slack: 'disconnected', gong: 'disconnected' };

  const metricCards = [
    { title: 'Active Accounts', value: metrics.active_accounts, change: trends.accounts_change, icon: UsersIcon },
    { title: 'Risk Alerts', value: metrics.risk_alerts, change: trends.alerts_change, icon: ExclamationTriangleIcon },
    { title: 'Expansion Opportunities', value: metrics.expansion_opportunities, change: trends.opportunities_change, icon: ArrowTrendingUpIcon },
    { title: 'Health Score', value: `${Math.round(metrics.health_score)}/10`, change: `${trends.health_change > '0' ? '+' : ''}${trends.health_change}`, icon: HeartIcon },
  ];

  const handleActivityClick = (activity: any) => {
    console.log('=== CLICKED ACTIVITY DEBUG ===');
    console.log('Full activity object:', JSON.stringify(activity, null, 2));
    console.log('Activity type:', activity.activity_type);
    console.log('Has query_text:', !!activity.query_text);
    console.log('Has insight_type:', !!activity.insight_type);
    console.log('Activity ID:', activity.id);
    alert(`Clicked activity: ${activity.title} (Type: ${activity.activity_type})`);
    
    const activityType = activity.activity_type || 'insight';
    
    if (activityType === 'qbr') {
      // Route to QBR detail page
      if (activity.report_id && activity.customer_id) {
        navigate(`/customers/${activity.customer_id}/qbrs/${activity.report_id}`);
      } else if (activity.customer_id) {
        // Route to customer QBR list if no specific report
        navigate(`/customers/${activity.customer_id}/qbrs`);
      } else {
        // Fallback to general QBR page
        navigate('/qbrs');
      }
    } else if (activityType === 'insight' && activity.query_text) {
      // This is an MCP query - route to insights query page
      navigate(`/insights/cached/${activity.id}`);
    } else if (activityType === 'insight') {
      // This is an analytics insight - route to analytics page with filter
      navigate('/analytics', { 
        state: { 
          highlightInsight: activity.id,
          customerFilter: activity.customer_id 
        } 
      });
    } else {
      // Default fallback
      navigate('/analytics');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Home</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Welcome back! Here's what's happening with your accounts.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards.map((metric, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
              <metric.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold">{metric.value}</div>
              <p className="text-xs text-muted-foreground">{metric.change} from last month</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {activities.length > 0 ? activities.map((activity) => (
                <button
                  key={activity.id}
                  onClick={() => handleActivityClick(activity)}
                  className="flex items-center w-full text-left p-2 sm:p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className={`w-2 h-2 rounded-full mr-3 sm:mr-4 flex-shrink-0 ${
                    activity.priority === 'high' ? 'bg-destructive' : 'bg-yellow-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{activity.title}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{activity.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground ml-2 flex-shrink-0" />
                </button>
              )) : (
                <div className="text-center py-6">
                  <p className="text-muted-foreground">No recent activities</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ask a question in the search bar above to generate your first insight.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {integrations.slack === 'disconnected' && (
                <Button variant="outline" className="w-full justify-start h-auto p-3" onClick={() => navigate('/integrations')}>
                  <SlackIcon size={20} className="mr-3 text-muted-foreground flex-shrink-0" />
                  <div className="text-left min-w-0">
                    <div className="text-sm font-medium">Connect Slack</div>
                    <div className="text-xs text-muted-foreground">Start analyzing account conversations</div>
                  </div>
                </Button>
              )}
              {integrations.gong === 'disconnected' && (
                <Button variant="outline" className="w-full justify-start h-auto p-3" onClick={() => navigate('/integrations')}>
                  <GongIcon size={20} className="mr-3 text-muted-foreground flex-shrink-0" />
                  <div className="text-left min-w-0">
                    <div className="text-sm font-medium">Connect Gong</div>
                    <div className="text-xs text-muted-foreground">Analyze sales calls and meetings</div>
                  </div>
                </Button>
              )}
              <Button variant="outline" className="w-full justify-start h-auto p-3" onClick={() => navigate('/customers')}>
                <UsersIcon className="h-5 w-5 mr-3 text-muted-foreground flex-shrink-0" />
                <div className="text-left min-w-0">
                  <div className="text-sm font-medium">View All Accounts</div>
                  <div className="text-xs text-muted-foreground">Manage your account portfolio</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
